import { X, Loader2 } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { SystemLanguage } from "../../common/system-language";
import type { ReportType } from "../../api/reportService";

const REPORT_OPTIONS: { value: ReportType; labelKey: string }[] = [
  { value: "VIOLENCE", labelKey: "violence" },
  { value: "SEXUAL", labelKey: "sexual" },
  { value: "MINOR_PROTECTION", labelKey: "minorProtection" },
  { value: "BULLYING", labelKey: "bullying" },
  { value: "SPAM", labelKey: "spam" },
  { value: "PRIVACY", labelKey: "privacy" },
  { value: "PROPERTY", labelKey: "property" },
  { value: "AGE_CONTENT", labelKey: "ageContent" },
  { value: "OTHER", labelKey: "other" },
];

const translations: Record<string, Record<string, string>> = {
  en: {
    title: "Report Message",
    description: "Why are you reporting this message?",
    violence: "Violence & self-harm",
    sexual: "Sexual exploitation & abuse",
    minorProtection: "Child/teen exploitation",
    bullying: "Bullying & Harassment",
    spam: "SPAM, fraud & deception",
    privacy: "Privacy violation",
    property: "Intellectual property",
    ageContent: "Age inappropriate content",
    other: "Something else",
    commentPlaceholder: "Please describe the issue...",
    commentRequired: "Please provide a description",
    error: "Failed to submit report. Please try again.",
    cancel: "Cancel",
    submit: "Submit",
  },
  ar: {
    title: "الإبلاغ عن رسالة",
    description: "لماذا تبلغ عن هذه الرسالة؟",
    violence: "العنف وإيذاء النفس",
    sexual: "الاستغلال والاعتداء الجنسي",
    minorProtection: "استغلال الأطفال/المراهقين",
    bullying: "التنمر والمضايقة",
    spam: "رسائل مزعجة واحتيال",
    privacy: "انتهاك الخصوصية",
    property: "الملكية الفكرية",
    ageContent: "محتوى غير مناسب للعمر",
    other: "شيء آخر",
    commentPlaceholder: "يرجى وصف المشكلة...",
    commentRequired: "يرجى تقديم وصف",
    error: "فشل إرسال البلاغ. يرجى المحاولة مرة أخرى.",
    cancel: "إلغاء",
    submit: "إرسال",
  },
};

export interface ReportModalProps {
  open: boolean;
  conversationId: string;
  onClose: () => void;
  onSubmit: (conversationId: string, reportType: ReportType, comment?: string) => Promise<void>;
  systemLanguage?: SystemLanguage;
}

export function ReportModal({ open, conversationId, onClose, onSubmit, systemLanguage = "en" }: ReportModalProps) {
  const isRTL = systemLanguage === "ar";
  const t = (key: string) => translations[systemLanguage]?.[key] || translations.en[key] || key;

  const [selectedType, setSelectedType] = useState<ReportType | null>(null);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!selectedType) return;
    if (selectedType === "OTHER" && !comment.trim()) {
      setError(t("commentRequired"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(conversationId, selectedType, selectedType === "OTHER" ? comment : undefined);
      onClose();
      setSelectedType(null);
      setComment("");
    } catch {
      setError(t("error"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    setComment("");
    setError(null);
    onClose();
  };

  const portalRoot = document.getElementById("root") || document.body;

  return createPortal(
    <div
      dir={isRTL ? "rtl" : "ltr"}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl p-6">
        <button
          onClick={handleClose}
          className={`absolute top-4 ${isRTL ? "left-4" : "right-4"} text-gray-400 hover:text-gray-600 transition`}
        >
          <X size={18} />
        </button>

        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {t("title")}
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          {t("description")}
        </p>

        <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
          {REPORT_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                selectedType === option.value
                  ? "border-[#33f] bg-[#33f]/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="reportType"
                value={option.value}
                checked={selectedType === option.value}
                onChange={() => setSelectedType(option.value)}
                className="w-4 h-4 text-[#33f] focus:ring-[#33f]"
              />
              <span className="text-sm text-gray-700">{t(option.labelKey)}</span>
            </label>
          ))}
        </div>

        {selectedType === "OTHER" && (
          <div className="mb-4">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("commentPlaceholder")}
              className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:border-[#33f]"
              rows={3}
            />
          </div>
        )}

        {error && (
          <p className="text-sm text-red-500 mb-4">{error}</p>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-2 px-4 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition"
          >
            {t("cancel")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedType || loading}
            className="flex-1 py-2 px-4 rounded-lg bg-[#33f] text-white text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {t("submit")}
          </button>
        </div>
      </div>
    </div>,
    portalRoot
  );
}
